/* eslint-disable no-console */
import AdvancedZPLGenerator from './AdvancedZPLGenerator';

/**
 * Professional Barcode Printer Helper
 * Supports professional barcode printers like Zebra, Brother, etc.
 * Uses ZPL, ESC/POS, and other professional printing protocols
 * kware: تحديث لاستخدام مولد ZPL المتقدم
 */
class ProfessionalPrinterHelper {
  constructor() {
    this.printerConnection = null;
    this.zplGenerator = new AdvancedZPLGenerator(); // kware: إضافة مولد ZPL المتقدم
    this.supportedPrinters = {
      ZEBRA_ZPL: 'zebra_zpl',
      ZEBRA_EPL: 'zebra_epl',
      BROTHER_PTOUCH: 'brother_ptouch',
      ESC_POS: 'esc_pos',
      GENERIC_TEXT: 'generic_text'
    };
  }

  /**
   * Check if professional printing is supported
   */
  isProfessionalPrintingSupported() {
    return 'serial' in navigator || 'usb' in navigator || 'bluetooth' in navigator;
  }

  /**
   * Generate advanced ZPL commands using the new generator
   * kware: استبدال مولد ZPL البسيط بالمولد المتقدم
   */
  generateZPLCommands(items, settings) {
    try {
      // Use the advanced ZPL generator
      return this.zplGenerator.generateAdvancedZPL(items, settings);
    } catch (error) {
      console.error('Advanced ZPL generation failed, falling back to simple method:', error);
      // Fallback to original method
      return this.generateSimpleZPLCommands(items, settings);
    }
  }

  /**
   * Fallback simple ZPL generation method
   * kware: طريقة احتياطية لتوليد ZPL بسيط
   */
  generateSimpleZPLCommands(items, settings) {
    const {
      format,
      copies,
      paperSize,
      orientation,
      includeText,
      labelWidth = 4, // inches
      labelHeight = 2 // inches
    } = settings;

    let zplCommands = '';

    items.forEach((item, index) => {
      for (let copy = 0; copy < copies; copy++) {
        zplCommands += this.generateSingleZPLLabel(item, settings, labelWidth, labelHeight);
      }
    });

    return zplCommands;
  }

  /**
   * Generate single ZPL label
   */
  generateSingleZPLLabel(item, settings, labelWidth, labelHeight) {
    const { format, includeText } = settings;
    const dpi = 203; // Standard Zebra printer DPI
    const widthDots = labelWidth * dpi;
    const heightDots = labelHeight * dpi;

    let zpl = '';

    // Start label
    zpl += '^XA\n';

    // Set label size
    zpl += `^LL${heightDots}\n`;
    zpl += `^PW${widthDots}\n`;

    // Barcode positioning and size
    const barcodeX = 50; // dots from left
    const barcodeY = 50; // dots from top
    const barcodeHeight = 100; // dots

    // Generate barcode based on format
    switch (format) {
      case 'CODE128':
        zpl += `^FO${barcodeX},${barcodeY}^BY3,2,${barcodeHeight}^BCN,,Y,N^FD${item.barcode}^FS\n`;
        break;
      case 'CODE39':
        zpl += `^FO${barcodeX},${barcodeY}^BY3,2,${barcodeHeight}^B3N,N,${barcodeHeight},Y,N^FD${item.barcode}^FS\n`;
        break;
      case 'EAN13':
        if (item.barcode.length === 13) {
          zpl += `^FO${barcodeX},${barcodeY}^BY3,2,${barcodeHeight}^BEN,${barcodeHeight},Y,N^FD${item.barcode}^FS\n`;
        } else {
          // Fallback to CODE128
          zpl += `^FO${barcodeX},${barcodeY}^BY3,2,${barcodeHeight}^BCN,,Y,N^FD${item.barcode}^FS\n`;
        }
        break;
      default:
        zpl += `^FO${barcodeX},${barcodeY}^BY3,2,${barcodeHeight}^BCN,,Y,N^FD${item.barcode}^FS\n`;
    }

    // Add text if requested
    if (includeText) {
      const textY = barcodeY + barcodeHeight + 20;
      zpl += `^FO${barcodeX},${textY}^A0N,25,25^FD${item.barcode}^FS\n`;

      // Add item info
      const infoY = textY + 40;
      zpl += `^FO${barcodeX},${infoY}^A0N,20,20^FD${item.materialType?.name || 'N/A'}^FS\n`;
      zpl += `^FO${barcodeX},${infoY + 30}^A0N,20,20^FD${item.effectiveLocation?.name || 'N/A'}^FS\n`;
    }

    // End label
    zpl += '^XZ\n';

    return zpl;
  }

  /**
   * Generate ESC/POS commands for thermal printers
   */
  generateESCPOSCommands(items, settings) {
    const { copies, includeText } = settings;
    const commands = [];

    // Initialize printer
    commands.push(0x1B, 0x40); // ESC @ (Initialize)

    items.forEach(item => {
      for (let copy = 0; copy < copies; copy++) {
        // Print barcode
        commands.push(0x1D, 0x68, 0x64); // Set barcode height (100 dots)
        commands.push(0x1D, 0x77, 0x03); // Set barcode width

        if (includeText) {
          commands.push(0x1D, 0x48, 0x02); // Print HRI below barcode
        } else {
          commands.push(0x1D, 0x48, 0x00); // No HRI
        }

        // Print CODE128 barcode
        commands.push(0x1D, 0x6B, 0x49); // GS k I (CODE128)
        commands.push(item.barcode.length + 2); // Length
        commands.push(0x7B, 0x42); // {B for CODE128B

        // Add barcode data
        for (let i = 0; i < item.barcode.length; i++) {
          commands.push(item.barcode.charCodeAt(i));
        }

        // Line feed
        commands.push(0x0A);

        // Add item information if text is enabled
        if (includeText) {
          const info = `${item.materialType?.name || 'N/A'} - ${item.effectiveLocation?.name || 'N/A'}`;
          for (let i = 0; i < info.length; i++) {
            commands.push(info.charCodeAt(i));
          }
          commands.push(0x0A, 0x0A); // Double line feed
        }
      }
    });

    // Cut paper (if supported)
    commands.push(0x1D, 0x56, 0x41, 0x03);

    return new Uint8Array(commands);
  }

  /**
   * Generate Brother P-Touch commands
   */
  generateBrotherPTouchCommands(items, settings) {
    const { copies, includeText } = settings;
    const commands = [];

    // Brother P-Touch initialization
    commands.push(0x1B, 0x69, 0x61, 0x01); // Initialize
    commands.push(0x1B, 0x69, 0x21, 0x00); // Job start

    items.forEach(item => {
      for (let copy = 0; copy < copies; copy++) {
        // Set font size for barcode
        commands.push(0x1B, 0x69, 0x58, 0x2C, 0x00, 0x3C, 0x00); // Font size

        // Print barcode text
        const barcodeText = `*${item.barcode}*`; // Simple barcode representation
        for (let i = 0; i < barcodeText.length; i++) {
          commands.push(barcodeText.charCodeAt(i));
        }
        commands.push(0x0D); // CR

        if (includeText) {
          // Add readable text
          for (let i = 0; i < item.barcode.length; i++) {
            commands.push(item.barcode.charCodeAt(i));
          }
          commands.push(0x0D); // CR

          // Item info
          const info = `${item.materialType?.name || 'N/A'}`;
          for (let i = 0; i < Math.min(info.length, 20); i++) { // Limit length
            commands.push(info.charCodeAt(i));
          }
          commands.push(0x0D); // CR
        }

        commands.push(0x0C); // Form feed (print and cut)
      }
    });

    return new Uint8Array(commands);
  }

  /**
   * Connect to printer via WebUSB
   */
  async connectViaBrowserPrinting(printerType = 'ZEBRA_ZPL') {
    try {
      if ('serial' in navigator) {
        // Try Serial API first (for USB-to-Serial adapters)
        return await this.connectViaSerial(printerType);
      } else if ('usb' in navigator) {
        // Fallback to WebUSB
        return await this.connectViaWebUSB(printerType);
      } else {
        throw new Error('No supported connection method available');
      }
    } catch (error) {
      console.error('Printer connection error:', error);
      throw error;
    }
  }

  /**
   * Connect via Web Serial API
   */
  async connectViaSerial(printerType) {
    try {
      const port = await navigator.serial.requestPort();
      await port.open({
        baudRate: 9600,
        dataBits: 8,
        parity: 'none',
        stopBits: 1
      });

      this.printerConnection = {
        type: 'serial',
        port,
        printerType
      };

      return {
        success: true,
        message: 'Connected to printer via Serial',
        connectionType: 'serial'
      };
    } catch (error) {
      throw new Error(`Serial connection failed: ${error.message}`);
    }
  }

  /**
   * Connect via WebUSB
   */
  async connectViaWebUSB(printerType) {
    try {
      const device = await navigator.usb.requestDevice({
        filters: [
          { vendorId: 0x0A5F }, // Zebra Technologies
          { vendorId: 0x04F9 }, // Brother Industries
        ]
      });

      await device.open();
      await device.selectConfiguration(1);
      await device.claimInterface(0);

      this.printerConnection = {
        type: 'usb',
        device,
        printerType
      };

      return {
        success: true,
        message: 'Connected to printer via USB',
        connectionType: 'usb'
      };
    } catch (error) {
      throw new Error(`USB connection failed: ${error.message}`);
    }
  }

  /**
   * Send data to connected printer
   */
  async sendToPrinter(data) {
    if (!this.printerConnection) {
      throw new Error('No printer connected');
    }

    try {
      if (this.printerConnection.type === 'serial') {
        const writer = this.printerConnection.port.writable.getWriter();

        if (typeof data === 'string') {
          await writer.write(new TextEncoder().encode(data));
        } else {
          await writer.write(data);
        }

        writer.releaseLock();
      } else if (this.printerConnection.type === 'usb') {
        const device = this.printerConnection.device;

        if (typeof data === 'string') {
          await device.transferOut(1, new TextEncoder().encode(data));
        } else {
          await device.transferOut(1, data);
        }
      }

      return { success: true };
    } catch (error) {
      throw new Error(`Print failed: ${error.message}`);
    }
  }

  /**
   * Professional print execution
   */
  async executeProfessionalPrint(items, settings) {
    try {
      const { printerType = 'ZEBRA_ZPL' } = settings;

      // Generate appropriate commands based on printer type
      let printData;
      switch (printerType) {
        case 'ZEBRA_ZPL':
          printData = this.generateZPLCommands(items, settings);
          break;
        case 'ESC_POS':
          printData = this.generateESCPOSCommands(items, settings);
          break;
        case 'BROTHER_PTOUCH':
          printData = this.generateBrotherPTouchCommands(items, settings);
          break;
        default:
          printData = this.generateZPLCommands(items, settings);
      }

      // Connect if not already connected
      if (!this.printerConnection) {
        await this.connectViaBrowserPrinting(printerType);
      }

      // Send to printer
      await this.sendToPrinter(printData);

      return {
        success: true,
        message: `Successfully printed ${items.length} barcodes on professional printer`,
        itemCount: items.length,
        printerType
      };
    } catch (error) {
      console.error('Professional print error:', error);
      return {
        success: false,
        message: error.message,
        error
      };
    }
  }

  /**
   * Generate downloadable print file
   */
  generatePrintFile(items, settings) {
    const { printerType = 'ZEBRA_ZPL' } = settings;

    let content; let filename; let
      mimeType;

    switch (printerType) {
      case 'ZEBRA_ZPL':
        content = this.generateZPLCommands(items, settings);
        filename = `barcodes_${Date.now()}.zpl`;
        mimeType = 'text/plain';
        break;
      case 'ESC_POS':
        content = this.generateESCPOSCommands(items, settings);
        filename = `barcodes_${Date.now()}.bin`;
        mimeType = 'application/octet-stream';
        break;
      case 'BROTHER_PTOUCH':
        content = this.generateBrotherPTouchCommands(items, settings);
        filename = `barcodes_${Date.now()}.lbx`;
        mimeType = 'application/octet-stream';
        break;
      default:
        content = this.generateZPLCommands(items, settings);
        filename = `barcodes_${Date.now()}.zpl`;
        mimeType = 'text/plain';
    }

    return {
      content,
      filename,
      mimeType,
      downloadUrl: this.createDownloadUrl(content, mimeType)
    };
  }

  /**
   * Create download URL
   */
  createDownloadUrl(content, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    return URL.createObjectURL(blob);
  }

  /**
   * Disconnect from printer
   */
  async disconnect() {
    if (this.printerConnection) {
      try {
        if (this.printerConnection.type === 'serial') {
          await this.printerConnection.port.close();
        } else if (this.printerConnection.type === 'usb') {
          await this.printerConnection.device.close();
        }
      } catch (error) {
        console.error('Disconnect error:', error);
      } finally {
        this.printerConnection = null;
      }
    }
  }

  /**
   * Get enhanced supported printer types with templates
   * kware: إضافة دعم قوالب ومعلومات محسنة للطابعات
   */
  getSupportedPrinterTypes() {
    const templateOptions = this.zplGenerator.getTemplateOptions();
    const printerProfiles = this.zplGenerator.getPrinterProfileOptions();

    return [
      {
        value: 'ZEBRA_ZPL',
        label: 'Zebra ZPL (ZT410, ZT420, etc.)',
        description: 'Zebra thermal printers using ZPL commands',
        templates: templateOptions.filter(t => t.value.includes('zebra')),
        profile: printerProfiles.find(p => p.value === 'ZEBRA_ZPL'),
        features: ['High Resolution', 'Multiple Formats', 'Templates', 'Border Support']
      },
      {
        value: 'ZEBRA_EPL',
        label: 'Zebra EPL (Legacy)',
        description: 'Older Zebra printers using EPL commands',
        templates: templateOptions.filter(t => t.value.includes('compact')),
        profile: printerProfiles.find(p => p.value === 'ZEBRA_EPL'),
        features: ['Basic Commands', 'Simple Layouts']
      },
      {
        value: 'ESC_POS',
        label: 'ESC/POS Thermal Printers',
        description: 'Generic thermal printers (Epson, Star, etc.)',
        templates: templateOptions.filter(t => t.value.includes('compact')),
        profile: printerProfiles.find(p => p.value === 'ESC_POS'),
        features: ['Wide Compatibility', 'Receipt Format']
      },
      {
        value: 'BROTHER_PTOUCH',
        label: 'Brother P-Touch Label Printers',
        description: 'Brother label printers (PT series)',
        templates: templateOptions.filter(t => t.value.includes('compact')),
        features: ['Label Tapes', 'Compact Format']
      }
    ];
  }

  /**
   * Get available label templates
   */
  getAvailableTemplates() {
    return this.zplGenerator.getTemplateOptions();
  }

  /**
   * Get printer profiles
   */
  getPrinterProfiles() {
    return this.zplGenerator.getPrinterProfileOptions();
  }

  /**
   * Validate printer settings
   */
  validatePrinterSettings(settings) {
    const errors = [];
    const warnings = [];

    // Check template compatibility
    if (settings.templateId && settings.printerType) {
      const template = this.zplGenerator.getTemplate(settings.templateId);
      const profile = this.zplGenerator.getPrinterProfile(settings.printerType);

      if (template.labelWidth > profile.maxWidth) {
        errors.push(`Label width ${template.labelWidth}" exceeds printer maximum ${profile.maxWidth}"`);
      }

      if (template.labelHeight > profile.maxHeight) {
        errors.push(`Label height ${template.labelHeight}" exceeds printer maximum ${profile.maxHeight}"`);
      }

      // Check barcode format support
      if (settings.format && !profile.supportedFormats.includes(settings.format)) {
        warnings.push(`Barcode format ${settings.format} may not be supported by ${profile.name}`);
      }
    }

    // Check print speed
    if (settings.printSpeed) {
      const profile = this.zplGenerator.getPrinterProfile(settings.printerType);
      if (profile.speedRange && (settings.printSpeed < profile.speedRange[0] || settings.printSpeed > profile.speedRange[1])) {
        warnings.push(`Print speed ${settings.printSpeed} is outside recommended range ${profile.speedRange[0]}-${profile.speedRange[1]}`);
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }
}

export default ProfessionalPrinterHelper;
